import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import connectApi from 'api/connectApi'
import {
    constants,
    getStartOfDayTimestamp,
    internetConnect,
    sendMessageToWorkerWithResponse
} from 'helpers'
import { getInteractionTimestamp } from 'helpers/interactions/interactionHelper'
import {
    flattenClientOrdersPayload,
    getDailyEnrolledClientCodes,
    getDailyKey
} from 'helpers/enrollmentRecapOrderHelpers'
import {
    EnrollmentRecapItem,
    EnrollmentRecapSliceNames,
    EnrollmentRecapState,
    InteractionPayloadType,
    Order
} from 'storeTypes'
import { InteractionOutcomes } from 'types'

const UNKNOWN_SOURCE = 'UNKNOWN'
const CONNECT_SOURCE = 'CONNECT'
const SIMPLE_ENROLLMENT_SOURCE = 'SIMPLE_ENROLLMENT'

// Survey names identifying which system recorded an ENROLLED interaction.
// Deliberately not read from the interaction's own `source` field -- that
// depends on every Connect call site remembering to set it, whereas these
// survey names are the ones each system has always used regardless.
const CONNECT_ENROLLED_SURVEY_NAMES = new Set([
    constants.TARGET_LIST_SURVEY_NAME,
    constants.BULK_ENROLLMENT_SURVEY_NAME
])
// Verified against myagro-mobile's own INTERACTION_SURVEY_NAME constant
// (farmer/checkout/enrollment.py, post_enrollment_interaction) -- that is
// the exact string Simple Enrollment/Bindal Sa Bopp sets on its own
// confirmed-order ENROLLED interaction, in a separate repo with no shared
// type/constant to import from.
const SIMPLE_ENROLLMENT_SURVEY_NAME = 'wave_checkout_enrollment'

const sourceForSurveyName = (surveyName?: string): string => {
    if (surveyName === SIMPLE_ENROLLMENT_SURVEY_NAME) {
        return SIMPLE_ENROLLMENT_SOURCE
    }
    if (surveyName && CONNECT_ENROLLED_SURVEY_NAMES.has(surveyName)) {
        return CONNECT_SOURCE
    }
    return UNKNOWN_SOURCE
}

/** client_code -> source, from today's ENROLLED interactions. Used only
 * to label a confirmed enrollment, never to decide whether one happened --
 * an ENROLLED interaction can be recorded without a package ever being
 * created (e.g. a shortcut button), so membership comes from orders. */
const sourceByClientCode = (interactions: InteractionPayloadType[]) => {
    const bySource: Record<string, string> = {}
    interactions
        .filter(
            (interaction) => interaction.outcome === InteractionOutcomes.ENROLLED
        )
        .forEach((interaction) => {
            if (interaction.client_code) {
                bySource[interaction.client_code] = sourceForSurveyName(
                    interaction.survey_name
                )
            }
        })
    return bySource
}

const countBySource = (
    enrolledClientCodes: Set<string>,
    interactions: InteractionPayloadType[]
) => {
    const clientSource = sourceByClientCode(interactions)
    const sourceCounts: Record<string, number> = {}
    enrolledClientCodes.forEach((clientCode) => {
        const source = clientSource[clientCode] || UNKNOWN_SOURCE
        sourceCounts[source] = (sourceCounts[source] || 0) + 1
    })
    return { sourceCounts }
}

/** Village-scoped orders, live when online so a poll tick reflects
 * enrollments made anywhere (any device, any channel) -- not just
 * whatever this device's local cache happened to sync last. Falls back
 * to the local IndexedDB cache when offline. */
const fetchTodaysOrders = async (villageId: string) => {
    if (internetConnect()) {
        const { CLIENT_ORDERS } = constants.endpoints
        const response = await connectApi.get(
            `${CLIENT_ORDERS}?village_ids=${villageId}&active_only=true`
        )
        // connectApi's response interceptor resolves HTTP error responses
        // instead of rejecting, so a 4xx/5xx lands here. Throw rather than
        // return [] -- an empty list would fulfil the thunk with zero counts
        // and overwrite the last-known-good numbers on screen.
        if (response.status !== 200) {
            throw new Error(
                `Orders request failed with status ${response.status}`
            )
        }
        return response.data.data as Order[]
    }

    const ordersResponse = await sendMessageToWorkerWithResponse({
        'get all client orders': true
    })
    return flattenClientOrdersPayload(ordersResponse)
}

const fetchTodaysInteractions = async (villageId: string) => {
    const createdSince = getStartOfDayTimestamp()
    const { INTERACTIONS } = constants.endpoints

    if (internetConnect()) {
        const response = await connectApi.get(
            `${INTERACTIONS}?village_ids=${villageId}&created_since=${createdSince}&dedupe_by_outcome=true`
        )
        return response.data as InteractionPayloadType[]
    }

    const { storedData } = await sendMessageToWorkerWithResponse({
        'get interactions': { villageIds: [villageId] }
    })
    return (storedData as InteractionPayloadType[]).filter(
        (interaction) =>
            getInteractionTimestamp(interaction) >= createdSince * 1000
    )
}

export const fetchEnrollmentRecapAction = createAsyncThunk(
    EnrollmentRecapSliceNames.FETCH_ENROLLMENT_RECAP,
    async ({ villageId }: { villageId: string }) => {
        const dailyKey = getDailyKey()

        const orders = await fetchTodaysOrders(villageId)
        const enrolledClientCodes = getDailyEnrolledClientCodes(
            orders,
            villageId,
            dailyKey
        )

        if (enrolledClientCodes.size === 0) {
            return { sourceCounts: {} }
        }

        const interactions = await fetchTodaysInteractions(villageId)
        return countBySource(enrolledClientCodes, interactions)
    }
)

const initialState: EnrollmentRecapState = {}

const getInitialRecapState = (): EnrollmentRecapItem => ({
    loading: false,
    error: '',
    hasLoadedOnce: false,
    sourceCounts: {}
})

const getOrInitVillageState = (
    state: EnrollmentRecapState,
    villageId: string
): EnrollmentRecapItem => {
    if (!state[villageId]) {
        state[villageId] = getInitialRecapState()
    }
    return state[villageId]
}

export const enrollmentRecapSlice = createSlice({
    name: EnrollmentRecapSliceNames.ENROLLMENT_RECAP,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEnrollmentRecapAction.pending, (state, action) => {
                const { villageId } = action.meta.arg
                const villageState = getOrInitVillageState(state, villageId)
                // A background poll refresh shouldn't blank out numbers
                // already on screen -- only show the loading state on the
                // very first fetch for this village.
                if (!villageState.hasLoadedOnce) {
                    villageState.loading = true
                }
                villageState.error = ''
            })
            .addCase(fetchEnrollmentRecapAction.fulfilled, (state, action) => {
                const { villageId } = action.meta.arg
                const villageState = getOrInitVillageState(state, villageId)
                villageState.loading = false
                villageState.hasLoadedOnce = true
                villageState.sourceCounts = action.payload.sourceCounts
            })
            .addCase(fetchEnrollmentRecapAction.rejected, (state, action) => {
                const { villageId } = action.meta.arg
                const villageState = getOrInitVillageState(state, villageId)
                villageState.loading = false
                // Keep whatever numbers we already have on screen -- a
                // failed background refresh (e.g. low/no connectivity)
                // should never wipe out the last-known-good counts.
                villageState.error = villageState.hasLoadedOnce
                    ? ''
                    : action.error.message || 'Un problème est survenu'
            })
    }
})

export const enrollmentRecapReducer = enrollmentRecapSlice.reducer
